import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Panel, Stat } from "../components/common";
import { ScoreGauge, ScoreBar } from "../components/ScoreGauge";
import { scoreMeta, chgColor, pct, fmt, regimeColor, classColors, corrColor } from "../lib/format";
import { Gauge, ShieldAlert, Flame, TrendingDown, Grid3x3, Radio, Layers, Activity, ArrowUpRight, ArrowDownRight, Bell, Globe } from "lucide-react";

function OppRow({ a, nav }) {
  const meta = scoreMeta(a.smart_quant_score);
  return (
    <button data-testid={`opp-row-${a.symbol}`} onClick={() => nav(`/asset/${a.symbol}`)}
      className="w-full hover-row flex items-center gap-3 px-2 py-2 rounded text-left">
      <span className="font-mono font-bold text-sm w-14">{a.symbol}</span>
      <span className="flex-1 truncate text-xs text-slate-400">{a.name}</span>
      <span className="tnum font-mono text-xs" style={{ color: chgColor(a.change_1d) }}>{pct(a.change_1d)}</span>
      <span className="font-mono font-bold tnum text-sm w-9 text-right" style={{ color: meta.color }}>{Math.round(a.smart_quant_score)}</span>
    </button>
  );
}

export default function Home() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);

  if (!d) return <div className="font-mono text-sm text-slate-500 animate-pulse">loading terminal…</div>;
  const { regime, risk, top_opportunities, worst_opportunities, heatmap, correlation, macro_changes, alerts } = d;
  const rc = regimeColor(regime.regime);
  const factorArr = Object.entries(regime.factors);
  const transArr = Object.entries(regime.transitions).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-6" data-testid="home-dashboard">
      <div>
        <div className="eyebrow">command center</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Macro Command Center</h1>
      </div>

      {/* Regime & Rating Alerts */}
      {alerts && alerts.alerts?.length > 0 && (
        <Panel testid="alerts-panel" title="Regime & Rating Alerts" icon={<Bell size={13} />}
          right={<span className="font-mono text-[11px] text-slate-500">{alerts.upgrades} up · {alerts.downgrades} down · {alerts.lookback_days}d</span>}>
          {alerts.alerts.some((a) => a.type === "regime") && (
            <div data-testid="regime-flip-banner" className="space-y-2 mb-3">
              {alerts.alerts.filter((a) => a.type === "regime").map((ra, i) => {
                const isEU = ra.region === "EA";
                const bc = isEU ? "#22D3EE" : rc;
                return (
                  <div key={i} data-testid={`regime-flip-banner-${ra.region || "US"}`} className="flex items-center gap-3 p-3 rounded-lg"
                    style={{ background: `${bc}12`, border: `1px solid ${bc}44` }}>
                    <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${bc}22` }}>
                      {isEU ? <Globe size={18} style={{ color: bc }} className="live-dot" /> : <Activity size={18} style={{ color: bc }} className="live-dot" />}
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-bold text-sm">{isEU ? "Eurozone" : "US"} regime shifted</div>
                      <div className="text-xs text-slate-400">
                        <span className="text-slate-300">{ra.from}</span> → <span style={{ color: bc }} className="font-semibold">{ra.to}</span>
                        {ra.months_ago ? ` · ~${ra.months_ago}mo ago` : ""} · confidence {ra.confidence}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {alerts.alerts.filter((a) => a.type === "rating").slice(0, 9).map((a, i) => (
              <button key={i} data-testid={`rating-alert-${a.symbol}`} onClick={() => nav(`/asset/${a.symbol}`)}
                className="flex items-center gap-2.5 p-2.5 rounded-lg panel-2 hover:border-[#334155] text-left transition-colors">
                <div className="h-8 w-8 rounded-md flex items-center justify-center shrink-0"
                  style={{ background: a.direction === "upgrade" ? "#10B98118" : "#EF444418" }}>
                  {a.direction === "upgrade" ? <ArrowUpRight size={16} className="text-[#10B981]" /> : <ArrowDownRight size={16} className="text-[#EF4444]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-mono font-bold text-sm">{a.symbol}</div>
                  <div className="text-[11px] text-slate-400 truncate">{a.from} → <span className="text-slate-200">{a.to}</span></div>
                </div>
                <div className="text-right font-mono">
                  <div className="text-xs font-bold" style={{ color: a.direction === "upgrade" ? "#10B981" : "#EF4444" }}>{a.score_now}</div>
                  <div className="text-[10px] text-slate-500 tnum">{a.score_prev}</div>
                </div>
              </button>
            ))}
          </div>
        </Panel>
      )}

      {/* Regime + Risk */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <Panel testid="regime-card" title="Current Economic Regime" icon={<Radio size={13} />} className="xl:col-span-2"
          right={<span className="eyebrow">as of {regime.as_of}</span>}>
          <div className="flex flex-col md:flex-row gap-6 items-center">
            <div className="flex flex-col items-center">
              <ScoreGauge value={regime.confidence} size={130} label={false} testid="regime-confidence-gauge" />
              <div className="eyebrow mt-2">confidence</div>
            </div>
            <div className="flex-1 w-full">
              <div className="font-display text-2xl font-extrabold" style={{ color: rc }}>{regime.label}</div>
              <div className="flex gap-4 mt-1 text-xs font-mono text-slate-400">
                <span>growth <b style={{ color: regime.growth_direction === "rising" ? "#10B981" : "#EF4444" }}>{regime.growth_direction}</b></span>
                <span>inflation <b style={{ color: regime.inflation_direction === "rising" ? "#EF4444" : "#10B981" }}>{regime.inflation_direction}</b></span>
                <span>10Y-2Y <b className={regime.yield_curve_10y2y < 0 ? "text-[#EF4444]" : "text-[#10B981]"}>{fmt(regime.yield_curve_10y2y)}</b></span>
              </div>
              <div className="mt-4 space-y-2">
                {factorArr.map(([k, v]) => (
                  <div key={k} className="flex items-center gap-3">
                    <span className="w-20 text-[11px] font-mono uppercase text-slate-400">{k}</span>
                    <div className="flex-1"><ScoreBar value={v.score} /></div>
                    <span className="w-10 text-right font-mono text-xs tnum">{v.score}</span>
                    <span className="w-14 text-right text-[10px] font-mono" style={{ color: v.direction.includes("rising") || v.direction === "easing" ? "#10B981" : "#EF4444" }}>{v.direction}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-[#1E293B]">
            <div className="eyebrow mb-2">regime transition probability</div>
            <div className="flex flex-wrap gap-1.5">
              {transArr.map(([k, v]) => (
                <span key={k} className="text-[10px] font-mono px-2 py-1 rounded border"
                  style={{ borderColor: `${regimeColor(k)}44`, color: k === regime.regime ? regimeColor(k) : "#94A3B8", background: k === regime.regime ? `${regimeColor(k)}12` : "transparent" }}>
                  {k} {v}%
                </span>
              ))}
            </div>
          </div>
        </Panel>

        <Panel testid="risk-dashboard" title="Risk Dashboard" icon={<ShieldAlert size={13} />}>
          <div className="flex items-center gap-4">
            <ScoreGauge value={risk.systemic_risk_index} size={110} label={false} />
            <div>
              <div className="eyebrow">systemic risk index</div>
              <div className="font-display text-xl font-bold" style={{ color: risk.systemic_risk_index > 60 ? "#EF4444" : risk.systemic_risk_index > 40 ? "#F59E0B" : "#10B981" }}>
                {risk.systemic_risk_index > 60 ? "ELEVATED" : risk.systemic_risk_index > 40 ? "MODERATE" : "CALM"}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <Stat label="VIX" value={fmt(risk.vix)} color="#F59E0B" />
            <Stat label="Avg Realized Vol" value={`${fmt(risk.avg_realized_vol, 1)}%`} />
            <Stat label="Market Breadth" value={`${fmt(risk.market_breadth, 0)}%`} color={risk.market_breadth > 50 ? "#10B981" : "#EF4444"} />
            <Stat label="Yield Curve 10-2" value={fmt(risk.yield_curve)} color={risk.yield_curve < 0 ? "#EF4444" : "#10B981"} />
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs font-mono text-slate-400">
            <Activity size={13} className="text-[#00E5FF]" /> Economic Surprise: <b className="text-[#00E5FF]">{d.economic_surprise_score}</b>
          </div>
        </Panel>
      </div>

      {/* Heatmap */}
      <Panel testid="market-heatmap" title="Market Heatmap — Smart Quant Score" icon={<Grid3x3 size={13} />}>
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 xl:grid-cols-10 gap-2">
          {heatmap.map((h) => {
            const m = scoreMeta(h.smart_quant_score);
            return (
              <button key={h.symbol} data-testid={`heatmap-${h.symbol}`} onClick={() => nav(`/asset/${h.symbol}`)}
                className="rounded-md p-2.5 text-left transition-transform hover:scale-[1.04] border"
                style={{ background: `${m.color}14`, borderColor: `${m.color}44` }}>
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold text-sm">{h.symbol}</span>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: classColors[h.asset_class] }} />
                </div>
                <div className="font-mono font-extrabold text-lg tnum" style={{ color: m.color }}>{Math.round(h.smart_quant_score)}</div>
                <div className="tnum font-mono text-[10px]" style={{ color: chgColor(h.change_1d) }}>{pct(h.change_1d)}</div>
              </button>
            );
          })}
        </div>
      </Panel>

      {/* Opportunities */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Panel testid="top-opportunities" title="Top Opportunities" icon={<Flame size={13} className="text-[#10B981]" />}>
          <div className="space-y-0.5">{top_opportunities.map((a) => <OppRow key={a.symbol} a={a} nav={nav} />)}</div>
        </Panel>
        <Panel testid="worst-opportunities" title="Worst / Highest Risk" icon={<TrendingDown size={13} className="text-[#EF4444]" />}>
          <div className="space-y-0.5">{worst_opportunities.map((a) => <OppRow key={a.symbol} a={a} nav={nav} />)}</div>
        </Panel>
      </div>

      {/* Correlation preview + macro changes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Panel testid="correlation-preview" title="Correlation Dashboard (60D)" icon={<Layers size={13} />} className="lg:col-span-2"
          right={<button onClick={() => nav("/correlations")} className="eyebrow text-[#00E5FF] hover:underline">expand →</button>}>
          <CorrMini corr={correlation} />
        </Panel>
        <Panel testid="macro-changes" title="Major Macro Changes" icon={<Radio size={13} />}>
          <div className="space-y-2">
            {macro_changes.map((m) => (
              <div key={m.name} className="flex items-center justify-between text-xs">
                <div>
                  <div className="font-medium text-slate-200">{m.name}</div>
                  <div className="eyebrow">{m.category}</div>
                </div>
                <div className="text-right font-mono tnum">
                  <div className="font-bold">{fmt(m.value)}</div>
                  <div style={{ color: chgColor(m.surprise) }}>surp {m.surprise > 0 ? "+" : ""}{fmt(m.surprise)}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function CorrMini({ corr }) {
  if (!corr) return null;
  const syms = corr.symbols.slice(0, 11);
  return (
    <div className="overflow-x-auto">
      <table className="border-collapse">
        <tbody>
          <tr>
            <td></td>
            {syms.map((s) => <td key={s} className="text-[9px] font-mono text-slate-500 px-1 rotate-0 text-center w-8">{s}</td>)}
          </tr>
          {syms.map((a) => (
            <tr key={a}>
              <td className="text-[9px] font-mono text-slate-500 pr-1 text-right">{a}</td>
              {syms.map((b) => {
                const v = corr.matrix[a][b];
                return <td key={b} className="w-8 h-6 text-center align-middle" style={{ background: corrColor(v) }}>
                  <span className="text-[8px] font-mono tnum text-slate-200">{v != null ? v.toFixed(1) : ""}</span>
                </td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
